import { afterAnimation, paperOf, penCurve, readsRtl, sigAnimate, sigEase, sigMs } from './motion'

/**
 * The rules write themselves (DESIGN.md "The pen: the title writes itself"):
 * a satr is uncovered from the reading start by paper-coloured covers that
 * shrink toward the reading end, then removed. The rule itself never changes.
 */

/** The ruled head a satr lies on: the nearest ancestor (at most two up) that draws the hairline. */
function ruleHost(satr: HTMLElement): HTMLElement | null {
  let node = satr.parentElement
  for (let i = 0; node && i < 2; i += 1, node = node.parentElement) {
    if (Number.parseFloat(getComputedStyle(node).borderBottomWidth) > 0) return node
    // Below 900px the hairline is the head's full-width ::after line.
    const after = getComputedStyle(node, '::after').content
    if (after && after !== 'none' && after !== 'normal') return node
  }
  return satr.parentElement
}

/** A paper-coloured cover lying over a stretch of the rule, inside the satr. */
function cover(satr: HTMLElement, paper: string, left: number, width: number, rtl: boolean) {
  const bottom = Number.parseFloat(getComputedStyle(satr).borderBottomWidth) || 2
  const span = document.createElement('span')
  span.setAttribute('aria-hidden', 'true')
  span.dataset.signature = 'rule-cover'
  Object.assign(span.style, {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: '3',
    left: `${left}px`,
    width: `${Math.max(0, width)}px`,
    bottom: `${-bottom - 0.5}px`,
    height: `${bottom + 1}px`,
    background: paper,
    transformOrigin: rtl ? 'left center' : 'right center',
  })
  satr.appendChild(span)
  return span
}

const ruled = new WeakMap<HTMLElement, number>()

/**
 * A satr writes itself from the reading start: a cover over the 2px rule and
 * one over the hairline beyond it each shrink toward the reading end. The
 * title's rule follows the pen line for line and its hairline is the
 * kashida; a section's rule is a plain stroke with its hairline after it.
 */
export function writeRule(satr: HTMLElement, delay: number, isTitle = false) {
  if (getComputedStyle(satr).position === 'static') return
  const box = satr.getBoundingClientRect()
  const host = ruleHost(satr)
  if (!box.width || !host) return
  const hostBox = host.getBoundingClientRect()
  const rtl = readsRtl(satr)
  const paper = paperOf(host)
  const ease = sigEase('--sig-ease-write')
  const run = (
    span: HTMLElement,
    frames: Keyframe[],
    duration: number,
    wait: number,
    easing: string,
  ) =>
    afterAnimation(
      sigAnimate(span, frames, { duration, delay: wait, easing, fill: 'backwards' }),
      () => span.remove(),
    )
  const shrink = [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }]

  const thick = cover(satr, paper, -satr.clientLeft, box.width, rtl)
  const restStart = rtl ? hostBox.left - box.left : box.width
  const restWidth = rtl ? box.left - hostBox.left : hostBox.right - box.right
  const rest = cover(satr, paper, restStart - satr.clientLeft, restWidth, rtl)
  if (isTitle) {
    run(
      thick,
      penCurve((p) => ({ transform: `scaleX(${1 - p})` })),
      sigMs('--sig-dur-pen', 620),
      delay,
      'linear',
    )
    run(
      rest,
      shrink,
      sigMs('--sig-dur-kashida', 360),
      delay + sigMs('--sig-delay-kashida', 520),
      ease,
    )
  } else {
    run(thick, shrink, sigMs('--sig-dur-stroke', 420), delay, ease)
    run(
      rest,
      shrink,
      sigMs('--sig-dur-hairline', 380),
      delay + sigMs('--sig-delay-hairline', 160),
      ease,
    )
  }
}

/**
 * Every section rule on screen writes itself, top to bottom, 60ms apart, at
 * most eight. A rule already written in this navigation is left alone.
 */
export function writeSectionRules(
  root: ParentNode,
  navigation: number,
  start: number,
  skip?: Element,
) {
  const step = sigMs('--sig-stagger', 60)
  const viewport = window.innerHeight
  let index = 0
  for (const satr of root.querySelectorAll<HTMLElement>('[data-satr]')) {
    if (index >= 8) break
    if (satr === skip || ruled.get(satr) === navigation) continue
    const box = satr.getBoundingClientRect()
    if (!box.width || box.bottom < 0 || box.top > viewport) continue
    ruled.set(satr, navigation)
    writeRule(satr, start + index * step)
    index += 1
  }
}
